import React from 'react';
import {
  Box,
  Skeleton,
  useTheme,
} from '@mui/material';
import styled from '@emotion/styled';
import { Theme } from '@mui/material/styles';

const CompanyCard = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  background-color: ${props => props.theme.palette.background.paper};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const ContentWrapper = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const CompanyDetails = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  justify-content: flex-start;
`;

const SkeletonLogo = styled(Skeleton)<{ theme: Theme }>`
  width: 120px;
  height: 120px;
  border-radius: 0.75rem;
  flex-shrink: 0;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    margin: 0;
  }
`;

const SkeletonText = styled(Skeleton)<{ theme: Theme }>`
  border-radius: 0.25rem;
`;

const SkeletonButton = styled(Skeleton)<{ theme: Theme }>`
  border-radius: 0.5rem;
  flex: 1;
  min-width: 120px;
  height: 36px;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex: 0 1 auto;
  }
`;

const ActionBar = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: row-reverse;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(
    to right,
    ${props => props.theme.palette.background.default},
    ${props => props.theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.03)' 
      : 'rgba(0, 0, 0, 0.02)'}
  );
  border-top: 1px solid ${props => props.theme.palette.divider};
  backdrop-filter: blur(8px);
`;

export const ComparisonCompanyCardSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <CompanyCard
      component="article"
      theme={theme}
    >
      <ContentWrapper theme={theme}>
        <SkeletonLogo variant="rectangular" theme={theme} />
        <CompanyDetails theme={theme}>
          <SkeletonText variant="text" width="60%" height={28} theme={theme} />
          <SkeletonText variant="text" width="90%" height={20} theme={theme} />
          <SkeletonText variant="text" width="40%" height={20} theme={theme} />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <SkeletonText variant="rectangular" width={100} height={24} theme={theme} />
            <SkeletonText variant="rectangular" width={100} height={24} theme={theme} />
          </Box>
        </CompanyDetails>
      </ContentWrapper>

      <ActionBar theme={theme}>
        <SkeletonButton variant="rectangular" theme={theme} />
      </ActionBar>
    </CompanyCard>
  );
}; 